import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Shield, Users, CheckCircle, AlertTriangle, Printer } from 'lucide-react';
import { Player } from '../../types/core';
import { DepthChartEntry } from '../../types/depth-chart';
import { SUBSTITUTION_STRATEGIES } from '../../constants/strategies';
import { SubstitutionReport as SubstitutionReportComponent } from './SubstitutionReport';
import { PlayerNameWithIcon } from '../ui/PlayerNameWithIcon';
import { supabase } from '../../lib/supabase';

interface SubstitutionPlan {
  position: string;
  starter: Player;
  backup: Player;
  rotation: {
    player: Player;
    situation: string;
    notes: string;
  }[];
  color?: string;
}

interface SubstitutionReport {
  formation: string;
  unit: 'offense' | 'defense';
  strategy: string;
  substitutions: SubstitutionPlan[];
}

interface SubstitutionStrategyProps {
  teamId: string;
  currentTeam: any;
  allPlayers: Player[];
  unavailablePlayers: Set<string>;
  depthChart: DepthChartEntry[];
  activeTab: string;
}

export function SubstitutionStrategy({ 
  teamId, 
  currentTeam, 
  allPlayers, 
  unavailablePlayers,
  depthChart,
  activeTab: initialActiveTab 
}: SubstitutionStrategyProps) {
  const [activeTab, setActiveTab] = useState<'offense' | 'defense'>(initialActiveTab.toLowerCase() as 'offense' | 'defense' || 'offense');
  const [currentStrategy, setCurrentStrategy] = useState<string>('');
  const [mprOnly, setMprOnly] = useState<boolean>(false);
  const [currentFormation, setCurrentFormation] = useState<string>('');
  const [offensivePlan, setOffensivePlan] = useState<SubstitutionReport | null>(null);
  const [defensivePlan, setDefensivePlan] = useState<SubstitutionReport | null>(null);

  // Get available formations from depth chart
  const getAvailableFormations = (unit: 'offense' | 'defense') => {
    const unitEntries = depthChart.filter(entry => 
      entry.unit.toLowerCase() === unit.toLowerCase() &&
      entry.formation &&
      entry.player_id &&
      !unavailablePlayers.has(entry.player_id)
    );

    const formations = [...new Set(unitEntries.map(entry => entry.formation))].filter(Boolean);
    return formations.map(formation => ({
      id: formation,
      name: formation
    }));
  };

  // Get available players from depth chart based on player availability and injury status
  const getAvailablePlayers = (unit: 'offense' | 'defense', formation: string) => {
    // Filter depth chart entries for current unit and formation
    const unitEntries = depthChart.filter(entry => 
      entry.unit.toLowerCase() === unit.toLowerCase() &&
      entry.formation === formation &&
      entry.player_id &&
      !unavailablePlayers.has(entry.player_id)
    );

    // Additional filter to exclude players marked as "Out" due to injury
    const availableEntries = unitEntries.filter(entry => {
      const player = allPlayers.find(p => p.id === entry.player_id);
      return player && player.injury_status !== 'O'; // Exclude players marked as "Out"
    });

    console.log(`Getting players for ${unit} - ${formation}:`, availableEntries.length, 'entries (after injury filter)');

    // Group players by position
    const playersByPosition = availableEntries.reduce((acc, entry) => {
      const player = allPlayers.find(p => p.id === entry.player_id);
      if (!player) return acc;

      if (!acc[entry.position]) {
        acc[entry.position] = [];
      }
      acc[entry.position].push({
        player,
        depth: entry.depth,
        color: entry.color
      });
      return acc;
    }, {} as Record<string, { player: Player; depth: number; color?: string }[]>);

    console.log(`Players by position:`, Object.keys(playersByPosition).length, 'positions');

    // Filter for MPR players if needed
    if (mprOnly) {
      // Find positions that have at least one MPR player
      const positionsWithMPR = new Set<string>();
      const allMPRPlayers: { position: string; player: Player; depth: number }[] = [];
      
      Object.entries(playersByPosition).forEach(([position, players]) => {
        const hasMPRPlayer = players.some(({ player }) => player.playParticipation?.hasMPR);
        if (hasMPRPlayer) {
          positionsWithMPR.add(position);
          // Track all MPR players and their positions
          players.forEach(({ player, depth }) => {
            if (player.playParticipation?.hasMPR) {
              allMPRPlayers.push({ position, player, depth });
            }
          });
        }
      });

      // Keep only positions that have MPR players, but keep all players in those positions
      Object.keys(playersByPosition).forEach(position => {
        if (!positionsWithMPR.has(position)) {
          delete playersByPosition[position];
        }
      });
      
      console.log(`After MPR filter (keeping positions with MPR players):`, Object.keys(playersByPosition).length, 'positions');
      console.log(`Positions with MPR players:`, Array.from(positionsWithMPR));
      console.log(`All MPR players found:`, allMPRPlayers.map(({ position, player, depth }) => ({
        position,
        name: `${player.first_name} ${player.last_name}`,
        depth,
        required: player.playParticipation?.minimumRequiredPlays || 0
      })));
    }

    return playersByPosition;
  };

  // Get player summary statistics
  const getPlayerSummary = (unit: 'offense' | 'defense') => {
    console.log(`=== Getting player summary for ${unit} ===`);
    console.log('Total depth chart entries:', depthChart.length);
    console.log('Unavailable players:', Array.from(unavailablePlayers));
    
    const unitEntries = depthChart.filter(entry => 
      entry.unit.toLowerCase() === unit.toLowerCase() &&
      entry.player_id &&
      !unavailablePlayers.has(entry.player_id)
    );

    // Additional filter to exclude players marked as "Out" due to injury
    const availableEntries = unitEntries.filter(entry => {
      const player = allPlayers.find(p => p.id === entry.player_id);
      return player && player.injury_status !== 'O'; // Exclude players marked as "Out"
    });

    console.log(`${unit} unit entries after filtering:`, availableEntries.length);
    console.log(`${unit} entries:`, availableEntries.map(e => ({ unit: e.unit, player_id: e.player_id, position: e.position })));

    const availablePlayers = availableEntries
      .map(entry => allPlayers.find(p => p.id === entry.player_id))
      .filter(Boolean) as Player[];

    console.log(`${unit} available players mapped:`, availablePlayers.length);

    const uniquePlayers = Array.from(new Set(availablePlayers.map(p => p.id)))
      .map(id => availablePlayers.find(p => p.id === id))
      .filter(Boolean) as Player[];

    console.log(`${unit} unique players:`, uniquePlayers.length);
    console.log(`${unit} unique player names:`, uniquePlayers.map(p => `${p.first_name} ${p.last_name}`));

    const mprPlayers = uniquePlayers.filter(player => player.playParticipation?.hasMPR);
    
    console.log(`${unit} MPR players:`, mprPlayers.length);
    console.log(`${unit} MPR player names:`, mprPlayers.map(p => `${p.first_name} ${p.last_name}`));
    
    return {
      totalAvailable: uniquePlayers.length,
      mprCount: mprPlayers.length,
      mprPlayers: mprPlayers
    };
  };

  // Generate color sequence substitution plan using depth chart colors
  const generateColorSequencePlan = async (playersByPosition: Record<string, { player: Player; depth: number; color?: string }[]>) => {
    const substitutions: SubstitutionPlan[] = [];

    // Group players by their assigned colors from depth chart
    const playersByColor: Record<string, { position: string; players: { player: Player; depth: number }[] }[]> = {};

    Object.entries(playersByPosition).forEach(([position, players]) => {
      const sortedPlayers = players.sort((a, b) => a.depth - b.depth);
      
      sortedPlayers.forEach(({ player, depth, color }) => {
        const colorKey = color || 'Default';
        
        if (!playersByColor[colorKey]) {
          playersByColor[colorKey] = [];
        }
        
        // Find existing position group for this color
        let positionGroup = playersByColor[colorKey].find(group => group.position === position);
        if (!positionGroup) {
          positionGroup = { position, players: [] };
          playersByColor[colorKey].push(positionGroup);
        }
        
        positionGroup.players.push({ player, depth });
      });
    });

    console.log('Players grouped by color:', playersByColor);

    // Create color package cards
    Object.entries(playersByColor).forEach(([color, positionGroups]) => {
      // Create a card for each color showing all positions and players
      const allPlayersInColor = positionGroups.flatMap(group => 
        group.players.map(({ player }) => `${group.position}: ${player.first_name} ${player.last_name}`)
      );

      const playerList = allPlayersInColor.join('\n');
      const firstPlayer = positionGroups[0]?.players[0]?.player;
      const secondPlayer = positionGroups[0]?.players[1]?.player || positionGroups[1]?.players[0]?.player;

      if (firstPlayer) {
        substitutions.push({
          position: color,
          starter: firstPlayer,
          backup: secondPlayer || firstPlayer,
          rotation: [{
            player: firstPlayer,
            situation: `${color} Package`,
            notes: playerList
          }],
          color: color
        });
      }
    });

    // Build proper starting lineup and rotation logic
    try {
      // Get starting lineup (depth=1 players with their colors and positions)
      const { data: startingLineup, error: startingError } = await supabase
        .from('depth_chart')
        .select('position, color, player_id, depth')
        .eq('team_id', teamId)
        .eq('unit', activeTab)
        .eq('formation', currentFormation)
        .eq('depth', 1);

      if (startingError) {
        console.error('Error fetching starting lineup:', startingError);
        return substitutions;
      }

      // Get backup players (depth=2 players with their colors and positions)
      const { data: backupLineup, error: backupError } = await supabase
        .from('depth_chart')
        .select('position, color, player_id, depth')
        .eq('team_id', teamId)
        .eq('unit', activeTab)
        .eq('formation', currentFormation)
        .eq('depth', 2);

      if (backupError) {
        console.error('Error fetching backup lineup:', backupError);
      }

      console.log('Starting lineup from DB:', startingLineup);
      console.log('Backup lineup from DB:', backupLineup);

      // Create position-to-color mapping for starters and backups
      const startingPositions: Record<string, { color: string; playerId: string }> = {};
      const backupPositions: Record<string, { color: string; playerId: string }> = {};

      startingLineup?.forEach(entry => {
        if (entry.color) {
          startingPositions[entry.position] = { color: entry.color, playerId: entry.player_id };
        }
      });

      backupLineup?.forEach(entry => {
        if (entry.color) {
          backupPositions[entry.position] = { color: entry.color, playerId: entry.player_id };
        }
      });

      // Get unique starting colors - fallback to using available colors if no depth=1 colors found
      let startingColors = [...new Set(startingLineup?.map(entry => entry.color).filter(Boolean))];
      const allColors = Object.keys(playersByColor).filter(color => color !== 'Default');
      
      // If no starting colors found from depth=1, use first half of available colors as "starting"
      if (startingColors.length === 0) {
        console.log('No starting colors found from depth=1, using fallback logic');
        startingColors = allColors.slice(0, Math.ceil(allColors.length / 2));
      }
      
      const backupColors = allColors.filter(color => !startingColors.includes(color));

      console.log('Starting colors (after fallback):', startingColors);
      console.log('Backup colors:', backupColors);
      console.log('All available colors:', allColors);

      // Generate rotation sequence if we have at least 2 colors total
      if (allColors.length >= 2) {
        console.log('Starting color rotation generation with', startingColors.length, 'starting colors');
        const rotationSequence: { player: Player; situation: string; notes: string }[] = [];
        const usedColorCombinations = new Set<string>(); // Track unique color combinations
        
        // Generate rotation scenarios
        const rotationScenarios = [
          'Starting Rotation',
          'First Substitution', 
          'Second Substitution',
          'Third Substitution',
          'Fourth Substitution',
          'Fifth Substitution',
          'Sixth Substitution',
          'Reset to Starting'
        ];

        for (let index = 0; index < rotationScenarios.length; index++) {
          const scenario = rotationScenarios[index];
          let currentColors: string[] = [];
          let isValidRotation = true;
          
          console.log(`\n=== Processing ${scenario} (index ${index}) ===`);
          
          if (index === 0) {
            // Initial: ALL starting colors (this is the true starting lineup)
            currentColors = [...startingColors];
            console.log('Initial setup - using all starting colors:', currentColors);
          } else if (index === rotationScenarios.length - 1) {
            // Reset: Back to starting colors
            currentColors = [...startingColors];
            console.log('Reset - back to starting colors:', currentColors);
          } else {
            // Create varied substitution patterns
            switch (index) {
              case 1: // First Substitution: Replace last starting color with first backup
                currentColors = [...startingColors.slice(0, 2), backupColors[0] || startingColors[2]].filter(Boolean);
                break;
              case 2: // Second Substitution: Replace first starting color with second backup  
                currentColors = [backupColors[1] || backupColors[0], startingColors[1], startingColors[2]].filter(Boolean);
                break;
              case 3: // Third Substitution: Replace middle starting color with third backup
                currentColors = [startingColors[0], backupColors[2] || backupColors[0], startingColors[2]].filter(Boolean);
                break;
              case 4: // Fourth Substitution: Use mix with different backup
                currentColors = [startingColors[0], backupColors[0], backupColors[1]].filter(Boolean);
                break;
              case 5: // Fifth Substitution: Another mix
                currentColors = [backupColors[0], startingColors[1], backupColors[2] || backupColors[1]].filter(Boolean);
                break;
              case 6: // Sixth Substitution: Third mix
                currentColors = [backupColors[1] || backupColors[0], backupColors[2] || startingColors[0], startingColors[0]].filter(Boolean);
                break;
              default:
                currentColors = [...startingColors.slice(0, 2), backupColors[0] || startingColors[2]].filter(Boolean);
                break;
            }
            console.log(`Substitution pattern ${index} - using colors:`, currentColors);
          }

          // Resolve position conflicts by strategically picking colors
          const positionToColor: Record<string, string> = {};
          const resolvedColors: string[] = [];
          
          // First pass: assign colors to positions they don't conflict with
          for (const color of currentColors) {
            if (playersByColor[color]) {
              let hasValidPositions = false;
              for (const positionGroup of playersByColor[color]) {
                const position = positionGroup.position;
                
                // If position not yet assigned, assign this color to it
                if (!positionToColor[position]) {
                  positionToColor[position] = color;
                  hasValidPositions = true;
                }
              }
              // Only include colors that have at least one valid position
              if (hasValidPositions && !resolvedColors.includes(color)) {
                resolvedColors.push(color);
              }
            }
          }

          // If we don't have enough colors (should be 3), add more from available colors
          if (resolvedColors.length < 3) {
            const availableColors = allColors.filter(color => !resolvedColors.includes(color));
            for (const color of availableColors) {
              if (resolvedColors.length >= 3) break;
              
              if (playersByColor[color]) {
                let canAddColor = false;
                // Check if this color has any positions not yet assigned
                for (const positionGroup of playersByColor[color]) {
                  const position = positionGroup.position;
                  if (!positionToColor[position]) {
                    positionToColor[position] = color;
                    canAddColor = true;
                  }
                }
                if (canAddColor) {
                  resolvedColors.push(color);
                }
              }
            }
          }

          // Update currentColors to resolved colors
          currentColors = resolvedColors.slice(0, 3); // Ensure exactly 3 colors
          console.log(`After conflict resolution - using colors:`, currentColors);
          console.log(`Position assignments:`, positionToColor);

          // Only add valid rotations with unique color combinations
          if (isValidRotation && currentColors.length > 0) {
            // Create a normalized combination string (sorted to ignore order)
            const normalizedCombination = [...currentColors].sort().join(',');
            
            if (!usedColorCombinations.has(normalizedCombination)) {
              const firstColorPlayer = playersByColor[currentColors[0]]?.[0]?.players[0]?.player;
              if (firstColorPlayer) {
                console.log(`Adding ${scenario} with unique colors:`, currentColors);
                console.log(`Normalized combination:`, normalizedCombination);
                usedColorCombinations.add(normalizedCombination);
                rotationSequence.push({
                  player: firstColorPlayer,
                  situation: scenario,
                  notes: currentColors.join(',') // Store colors in notes for display
                });
              } else {
                console.log(`No player found for ${scenario} with colors:`, currentColors);
              }
            } else {
              console.log(`Skipping ${scenario} - duplicate color combination:`, currentColors, 'normalized:', normalizedCombination);
            }
          } else {
            console.log(`Skipping ${scenario} - isValid: ${isValidRotation}, colors length: ${currentColors.length}`);
          }
        }

        // Add rotation sequence as a special entry
        if (rotationSequence.length > 0) {
          console.log('Adding rotation sequence with', rotationSequence.length, 'scenarios:', rotationSequence);
          substitutions.push({
            position: 'Rotation Sequence',
            starter: rotationSequence[0].player,
            backup: rotationSequence[1]?.player || rotationSequence[0].player,
            rotation: rotationSequence,
            color: 'sequence'
          });
          console.log('Rotation sequence added to substitutions. Total substitutions:', substitutions.length);
        } else {
          console.log('No rotation sequence generated - length was 0');
        }
      }
    } catch (error) {
      console.error('Error generating color rotation:', error);
    }

    return substitutions;
  };

  // Generate standard substitution plan - updated for MPR focus
  const generateStandardPlan = (playersByPosition: Record<string, { player: Player; depth: number; color?: string }[]>) => {
    const substitutions: SubstitutionPlan[] = [];

    // Get MPR players for snap-based calculations (excluding injured players)
    const mprPlayers = allPlayers.filter(player => 
      player.playParticipation?.hasMPR && 
      !unavailablePlayers.has(player.id) &&
      player.injury_status !== 'O' // Exclude players marked as "Out"
    );

    // Calculate MPR recommendations for snap-based strategy
    const calculateMPRRecommendations = () => {
      const totalMPRPlays = mprPlayers.reduce((total, player) => 
        total + (player.playParticipation?.minimumRequiredPlays || 0), 0
      );
      
      // Assume 4 quarters, distribute plays evenly with some flexibility
      const playsPerQuarter = Math.ceil(totalMPRPlays / 4);
      
      return {
        totalMPRPlays,
        playsPerQuarter,
        mprPlayers: mprPlayers.map(player => ({
          player,
          required: player.playParticipation?.minimumRequiredPlays || 0,
          recommendedPerQuarter: Math.ceil((player.playParticipation?.minimumRequiredPlays || 0) / 4)
        }))
      };
    };

    const mprRecommendations = calculateMPRRecommendations();

    // For package-based strategy, create distinct packages across all positions
    if (currentStrategy === 'package_based') {
      // Collect all available players by position
      const allPositionPlayers: Record<string, { player: Player; depth: number; color?: string }[]> = {};
      
      Object.entries(playersByPosition).forEach(([position, players]) => {
        allPositionPlayers[position] = players.sort((a, b) => a.depth - b.depth);
      });

      // Determine number of packages needed based on available depth levels
      const allDepths = Object.values(allPositionPlayers)
        .flat()
        .map(p => p.depth);
      const maxDepth = Math.max(...allDepths, 2); // At least 2 packages
      const numPackages = Math.min(maxDepth, 4); // Cap at 4 packages (Alpha, Bravo, Charlie, Delta)
      
      // Package names for up to 4 packages
      const packageNames = ['Alpha', 'Bravo', 'Charlie', 'Delta'];
      const playCounts = [6, 4, 3, 2]; // Decreasing play counts for each package
      
      // Create packages dynamically
      const packages = Array.from({ length: numPackages }, (_, index) => ({
        name: `${packageNames[index]} Package`,
        playCount: playCounts[index],
        players: {} as Record<string, Player>
      }));

      // Assign players to packages - each player goes to only one package based on depth
      Object.entries(allPositionPlayers).forEach(([position, players]) => {
        players.forEach((playerData, index) => {
          // Assign to package based on depth (depth 1 -> Alpha, depth 2 -> Bravo, etc.)
          const packageIndex = Math.min(playerData.depth - 1, numPackages - 1);
          if (packageIndex >= 0 && packageIndex < packages.length) {
            packages[packageIndex].players[position] = playerData.player;
          }
        });
      });

      // Create package substitutions
      packages.forEach((pkg) => {
        const packagePositions = Object.keys(pkg.players);
        if (packagePositions.length > 0) {
          const primaryPlayer = pkg.players[packagePositions[0]];
          
          substitutions.push({
            position: pkg.name,
            starter: primaryPlayer,
            backup: primaryPlayer,
            rotation: [
              {
                player: primaryPlayer,
                situation: `${pkg.name} (${pkg.playCount} plays)`,
                notes: Object.entries(pkg.players)
                  .map(([pos, player]) => `${pos}: ${player.first_name} ${player.last_name}`)
                  .join('\n')
              }
            ]
          });
        }
      });

      // Add rotation schedule with all packages
      const firstPlayer = allPositionPlayers[Object.keys(allPositionPlayers)[0]]?.[0]?.player;
      if (firstPlayer) {
        const rotationNotes = packages
          .map(pkg => `${pkg.name}: ${pkg.playCount} plays`)
          .join('\n\n') + '\n\nRepeat this pattern throughout the game';
          
        substitutions.push({
          position: 'Rotation Schedule',
          starter: firstPlayer,
          backup: firstPlayer,
          rotation: [
            {
              player: firstPlayer,
              situation: 'Game Plan',
              notes: rotationNotes
            }
          ]
        });
      }
    } else {
      // Handle other strategies (drive_based, snap_based)
      Object.entries(playersByPosition).forEach(([position, players]) => {
        const sortedPlayers = players.sort((a, b) => a.depth - b.depth);
        
        console.log(`[${currentStrategy}] Processing position ${position}:`, sortedPlayers.map(p => ({
          name: `${p.player.first_name} ${p.player.last_name}`,
          depth: p.depth,
          hasMPR: p.player.playParticipation?.hasMPR || false
        })));
        
        // For MPR-only mode, create plans even with single players
        const minPlayers = mprOnly ? 1 : 2;
        
        if (sortedPlayers.length >= minPlayers) {
          const starter = sortedPlayers[0].player;
          const backup = sortedPlayers[1]?.player || starter; // Use starter as backup if only one player

          let rotation: { player: Player; situation: string; notes: string }[] = [];
          
          if (mprOnly && sortedPlayers.length === 1) {
            // Special handling for single MPR players
            rotation = [
              { player: starter, situation: 'Full Game', notes: `${starter.first_name} ${starter.last_name} (MPR Required)` }
            ];
          } else {
            switch (currentStrategy) {
              case 'drive_based':
                if (mprOnly) {
                  // For MPR mode, include all available players in rotation to ensure all MPR players get playing time
                  const allPlayersForPosition = sortedPlayers.map(p => p.player);
                  const driveLabels = ['Drive 1', 'Drive 2', 'Drive 3', 'Drive 4'];
                  
                  rotation = driveLabels.map((driveLabel, index) => {
                    // Cycle through all available players for this position
                    const playerIndex = index % allPlayersForPosition.length;
                    const selectedPlayer = allPlayersForPosition[playerIndex];
                    
                                         return {
                       player: selectedPlayer,
                       situation: driveLabel,
                       notes: `${selectedPlayer.first_name} ${selectedPlayer.last_name}`
                     };
                  });
                } else {
                  // Standard drive_based strategy with just starter/backup
                  rotation = [
                    { player: starter, situation: 'Drive 1', notes: `${starter.first_name} ${starter.last_name}` },
                    { player: backup, situation: 'Drive 2', notes: `${backup.first_name} ${backup.last_name}` },
                    { player: starter, situation: 'Drive 3', notes: `${starter.first_name} ${starter.last_name}` },
                    { player: backup, situation: 'Drive 4', notes: `${backup.first_name} ${backup.last_name}` }
                  ];
                }
                break;
              case 'snap_based':
                // Get MPR requirements for this position's players
                const positionMPRPlayers = sortedPlayers.filter(({ player }) => 
                  player.playParticipation?.hasMPR
                );
                
                if (positionMPRPlayers.length > 0) {
                  // This position has MPR players - create detailed snap-based rotation
                  const mprPlayer = positionMPRPlayers[0];
                  const requiredPlays = mprPlayer.player.playParticipation?.minimumRequiredPlays || 0;
                  const playsPerQuarter = Math.ceil(requiredPlays / 4);
                  
                  rotation = [
                    { 
                      player: mprPlayer.player, 
                      situation: 'Q1 - MPR Focus', 
                      notes: `${mprPlayer.player.first_name} ${mprPlayer.player.last_name} (Target: ${playsPerQuarter} plays, MPR: ${requiredPlays} total)` 
                    },
                    { 
                      player: backup, 
                      situation: 'Q2 - Rotation', 
                      notes: `${backup.first_name} ${backup.last_name} (Relief for MPR player)` 
                    },
                    { 
                      player: mprPlayer.player, 
                      situation: 'Q3 - MPR Focus', 
                      notes: `${mprPlayer.player.first_name} ${mprPlayer.player.last_name} (Target: ${playsPerQuarter} plays, MPR: ${requiredPlays} total)` 
                    },
                    { 
                      player: backup, 
                      situation: 'Q4 - Rotation', 
                      notes: `${backup.first_name} ${backup.last_name} (Final relief, ensure MPR met)` 
                    }
                  ];
                } else {
                  // No MPR players in this position - standard quarter rotation
                  rotation = [
                    { player: starter, situation: 'First Quarter', notes: `${starter.first_name} ${starter.last_name}` },
                    { player: backup, situation: 'Second Quarter', notes: `${backup.first_name} ${backup.last_name}` },
                    { player: starter, situation: 'Third Quarter', notes: `${starter.first_name} ${starter.last_name}` },
                    { player: backup, situation: 'Fourth Quarter', notes: `${backup.first_name} ${backup.last_name}` }
                  ];
                }
                break;
            }
          }

          if (rotation.length > 0) {
            substitutions.push({
              position,
              starter,
              backup,
              rotation
            });
          }
        }
      });
    }

    // Add MPR summary for snap-based strategy
    if (currentStrategy === 'snap_based' && mprRecommendations.mprPlayers.length > 0) {
      const mprSummary = {
        position: 'MPR Summary',
        starter: mprRecommendations.mprPlayers[0].player,
        backup: mprRecommendations.mprPlayers[0].player,
        rotation: [
          {
            player: mprRecommendations.mprPlayers[0].player,
            situation: 'Total MPR Requirements',
            notes: `Total Required Plays: ${mprRecommendations.totalMPRPlays} | Recommended per Quarter: ${mprRecommendations.playsPerQuarter}`
          },
          ...mprRecommendations.mprPlayers.map(({ player, required, recommendedPerQuarter }) => ({
            player,
            situation: `${player.first_name} ${player.last_name} - MPR`,
            notes: `Required: ${required} plays | Per Quarter: ${recommendedPerQuarter} plays`
          }))
        ]
      };
      substitutions.push(mprSummary);
    }

    return substitutions;
  };

  // Generate substitution plan
  const generateSubstitutionPlan = async () => {
    if (!currentStrategy || !currentFormation) {
      toast.error('Please select both a strategy and formation');
      return;
    }

    const playersByPosition = getAvailablePlayers(activeTab, currentFormation);
    
    console.log(`Generating plan for ${activeTab} - ${currentFormation}:`, playersByPosition);
    
    if (Object.keys(playersByPosition).length === 0) {
      toast.error('No available players found for the selected formation');
        return;
      }

    let substitutions: SubstitutionPlan[];

    if (currentStrategy === 'color_sequence') {
      substitutions = await generateColorSequencePlan(playersByPosition);
    } else {
      substitutions = generateStandardPlan(playersByPosition);
    }

    console.log(`Generated ${substitutions.length} substitution plans:`, substitutions);

    if (substitutions.length === 0) {
      toast.error('No valid substitution plans could be generated');
      return;
    }

    const plan: SubstitutionReport = {
      formation: currentFormation,
      unit: activeTab,
      strategy: currentStrategy,
      substitutions
    };

    if (activeTab === 'offense') {
      setOffensivePlan(plan);
    } else {
      setDefensivePlan(plan);
    }

    toast.success(`Substitution plan generated successfully (${substitutions.length} segments)`);
  };

  // Handle strategy change
  const handleStrategyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentStrategy(e.target.value);
  };

  // Handle formation change
  const handleFormationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentFormation(e.target.value);
  };

  // Handle MPR toggle
  const handleMprToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMprOnly(e.target.checked);
  };

  // Handle tab change
  const handleTabChange = (tab: 'offense' | 'defense') => {
    setActiveTab(tab);
    setCurrentFormation('');
    setCurrentStrategy('');
  };

  // Auto-generate plan when all selections are made
  useEffect(() => {
    if (currentStrategy && currentFormation) {
      generateSubstitutionPlan();
    }
  }, [currentStrategy, currentFormation, mprOnly, activeTab]);

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById('substitution-plan-content');
    if (!printContent) {
      toast.error('No substitution plan to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Unable to open print window');
      return;
    }

    const strategyName = SUBSTITUTION_STRATEGIES.find(s => s.id === currentStrategy)?.name || currentStrategy;
    
    // Get all package/segment content and split into two columns
    let allContent = '';
    
    if (currentStrategy === 'color_sequence') {
      // For color sequence, get both packages and rotation sequence
      const packagesSection = printContent.querySelector('.color-packages-section');
      const rotationSection = printContent.querySelector('.rotation-sequence-section');
      
      console.log('Found packages section:', packagesSection);
      console.log('Found rotation section:', rotationSection);
      
      // Clone the sections to avoid modifying the original DOM
      const packagesClone = packagesSection?.cloneNode(true) as HTMLElement;
      const rotationClone = rotationSection?.cloneNode(true) as HTMLElement;
      
      // Ensure all content is properly captured by getting outerHTML
      allContent = (packagesClone?.outerHTML || '') + (rotationClone?.outerHTML || '');
      
      console.log('Packages section HTML:', packagesClone?.outerHTML);
      console.log('Rotation section HTML:', rotationClone?.outerHTML);
      console.log('Combined content length:', allContent.length);
    } else {
      // For other strategies, get the segment-based content
      const segmentSection = printContent.querySelector('.segment-based-section');
      const segmentClone = segmentSection?.cloneNode(true) as HTMLElement;
      allContent = segmentClone?.outerHTML || '';
    }

    // Split content into left and right columns for optimal page usage
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = allContent;
    
    // Debug: Log what content was captured
    console.log('Captured content for print:', allContent);
    console.log('Temp div content:', tempDiv.innerHTML);
    
    // Get all cards/segments - include both rotation cards and color package cards
    const rotationCards = Array.from(tempDiv.querySelectorAll('.bg-gray-50, .bg-blue-50'));
    const colorPackageCards = Array.from(tempDiv.querySelectorAll('.color-packages-section .p-4.border.rounded-lg'));
    const allCards = [...colorPackageCards, ...rotationCards];
    console.log('Found rotation cards:', rotationCards.length);
    console.log('Found color package cards:', colorPackageCards.length);
    console.log('Total cards for print:', allCards.length, allCards);
    
    const midpoint = Math.ceil(allCards.length / 2);
    
    // Split into two columns
    const leftColumnCards = allCards.slice(0, midpoint);
    const rightColumnCards = allCards.slice(midpoint);
    
    const leftColumnContent = leftColumnCards.map(card => card.outerHTML).join('');
    const rightColumnContent = rightColumnCards.map(card => card.outerHTML).join('');
    
    console.log('Left column cards:', leftColumnCards.length);
    console.log('Right column cards:', rightColumnCards.length);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Substitution Strategy - ${currentFormation} ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</title>
        <style>
          * { box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; 
            margin: 15px;
            color: #000;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Simple header */
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ccc;
          }
          
          /* Preserve all existing styles from the screen */
          .bg-white { background-color: white !important; }
          .rounded-lg { border-radius: 8px !important; }
          .shadow { box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
          .p-4 { padding: 16px !important; }
          .p-3 { padding: 12px !important; }
          .border-b { border-bottom: 1px solid #e5e7eb !important; }
          .border-t { border-top: 1px solid #e5e7eb !important; }
          .border { border: 1px solid #e5e7eb !important; }
          .border-gray-200 { border-color: #e5e7eb !important; }
          .border-gray-300 { border-color: #d1d5db !important; }
          .border-gray-400 { border-color: #9ca3af !important; }
          .border-blue-200 { border-color: #bfdbfe !important; }
          .bg-gray-50 { background-color: #f9fafb !important; }
          .bg-blue-50 { background-color: #eff6ff !important; }
          .text-lg { font-size: 18px !important; line-height: 28px !important; }
          .text-md { font-size: 16px !important; line-height: 24px !important; }
          .text-sm { font-size: 14px !important; line-height: 20px !important; }
          .text-xs { font-size: 12px !important; line-height: 16px !important; }
          .font-semibold { font-weight: 600 !important; }
          .font-bold { font-weight: 700 !important; }
          .font-medium { font-weight: 500 !important; }
          .text-gray-900 { color: #111827 !important; }
          .text-gray-800 { color: #1f2937 !important; }
          .text-gray-700 { color: #374151 !important; }
          .text-gray-600 { color: #4b5563 !important; }
          .text-blue-800 { color: #1e40af !important; }
          .mb-3 { margin-bottom: 12px !important; }
          .mb-4 { margin-bottom: 16px !important; }
          .mt-4 { margin-top: 16px !important; }
          .space-y-1 > * + * { margin-top: 4px !important; }
          .space-y-2 > * + * { margin-top: 8px !important; }
          .space-y-3 > * + * { margin-top: 12px !important; }
          .opacity-90 { opacity: 0.9 !important; }
          
          /* Grid layouts */
          .grid { display: grid !important; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .gap-4 { gap: 16px !important; }
          
          /* Flexbox */
          .flex { display: flex !important; }
          .items-center { align-items: center !important; }
          .justify-between { justify-content: space-between !important; }
          .gap-2 { gap: 8px !important; }
          .gap-3 { gap: 12px !important; }
          
          /* Color squares - maintain exact appearance */
          .w-8 { width: 32px !important; }
          .h-8 { height: 32px !important; }
          .w-6 { width: 24px !important; }
          .h-6 { height: 24px !important; }
          .rounded { border-radius: 4px !important; }
          .border-2 { border-width: 2px !important; }
          .shadow-sm { box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important; }
          
                     /* Color sequence specific styles */
           .color-packages-section { display: block !important; }
           .rotation-sequence-section { display: block !important; }
           .bg-gray-50 { background-color: #f9fafb !important; }
           .bg-blue-50 { background-color: #eff6ff !important; }
           .border { border: 1px solid #e5e7eb !important; }
           .border-gray-200 { border-color: #e5e7eb !important; }
           .border-blue-200 { border-color: #bfdbfe !important; }
           .rounded-lg { border-radius: 8px !important; }
           .p-4 { padding: 16px !important; }
           .p-3 { padding: 12px !important; }
           .mb-4 { margin-bottom: 16px !important; }
           .mt-4 { margin-top: 16px !important; }
           .space-y-2 > * + * { margin-top: 8px !important; }
           .space-y-3 > * + * { margin-top: 12px !important; }
           .space-y-4 > * + * { margin-top: 16px !important; }
           .gap-2 { gap: 8px !important; }
           .gap-3 { gap: 12px !important; }
           .gap-4 { gap: 16px !important; }
           .grid { display: grid !important; }
           .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
           .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
           .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
           .flex { display: flex !important; }
           .items-center { align-items: center !important; }
           .justify-between { justify-content: space-between !important; }
           .text-sm { font-size: 14px !important; line-height: 20px !important; }
           .text-md { font-size: 16px !important; line-height: 24px !important; }
           .text-lg { font-size: 18px !important; line-height: 28px !important; }
           .font-semibold { font-weight: 600 !important; }
           .font-medium { font-weight: 500 !important; }
           .text-gray-900 { color: #111827 !important; }
           .text-blue-800 { color: #1e40af !important; }
           .text-blue-900 { color: #1e3a8a !important; }
           
                       /* Compact color package tiles */
            .p-4.border.rounded-lg { 
              width: 200px !important; 
            }
          
          /* Print two-column layout */
          .print-layout { display: flex !important; gap: 20px !important; }
          .print-column { flex: 1 !important; }
          .print-column-left { flex: 0 0 45% !important; }
          .print-column-right { flex: 0 0 55% !important; }
          
          /* List styles */
          .space-y-1 > li { margin-bottom: 4px !important; }
          .w-8 { width: 32px !important; }
          
          /* Responsive adjustments for print */
          @media print {
            body { margin: 8px !important; font-size: 11px !important; }
            .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
            .text-lg { font-size: 14px !important; }
            .text-md { font-size: 12px !important; }
            .text-sm { font-size: 10px !important; }
            .text-xs { font-size: 9px !important; }
            .p-4 { padding: 8px !important; }
            .p-3 { padding: 6px !important; }
            .gap-4 { gap: 8px !important; }
            .gap-3 { gap: 6px !important; }
            .gap-2 { gap: 4px !important; }
            .mb-4 { margin-bottom: 8px !important; }
            .mb-3 { margin-bottom: 6px !important; }
            .mt-4 { margin-top: 8px !important; }
            .space-y-3 > * + * { margin-top: 6px !important; }
            .space-y-2 > * + * { margin-top: 4px !important; }
            .space-y-1 > * + * { margin-top: 2px !important; }
            
            /* Compact color squares */
            .w-8 { width: 24px !important; }
            .h-8 { height: 24px !important; }
            .w-6 { width: 18px !important; }
            .h-6 { height: 18px !important; }
            
            /* Compact header */
            .print-header { margin-bottom: 10px !important; padding-bottom: 5px !important; }
            .print-header h2 { font-size: 16px !important; margin: 0 !important; }
            
            /* Print layout adjustments */
            .print-layout { gap: 15px !important; }
            .print-column-left { flex: 0 0 40% !important; }
            .print-column-right { flex: 0 0 60% !important; }
            
            .no-print { display: none !important; }
            
            /* Force color printing */
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h2>${currentFormation} ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} - ${strategyName}</h2>
        </div>
        <div class="print-layout">
          <div class="print-column-left">
            ${leftColumnContent}
          </div>
          <div class="print-column-right">
            ${rightColumnContent}
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    
    // Wait a moment for content to fully render before printing
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };

  const availableFormations = getAvailableFormations(activeTab);
  const playerSummary = getPlayerSummary(activeTab);
  const currentPlan = activeTab === 'offense' ? offensivePlan : defensivePlan;

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('offense')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'offense'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Offense</span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('defense')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'defense'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Defense</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Player Summary */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-3">
          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Player Summary
        </h3>
        
        <div className="flex space-x-4">
          <div className="bg-blue-50 rounded px-3 py-2 text-center">
            <p className="text-xs font-medium text-blue-600">Total Available</p>
            <p className="text-lg font-bold text-blue-900">{playerSummary.totalAvailable}</p>
          </div>
          
          <div className="bg-amber-50 rounded px-3 py-2 text-center">
            <p className="text-xs font-medium text-amber-600">MPR Players</p>
            <p className="text-lg font-bold text-amber-900">{playerSummary.mprCount}</p>
                    </div>

          <div className="bg-gray-50 rounded px-3 py-2 text-center">
            <p className="text-xs font-medium text-gray-600">Formations</p>
            <p className="text-lg font-bold text-gray-900">{availableFormations.length}</p>
                </div>
              </div>

        {/* MPR Players List */}
        {playerSummary.mprPlayers.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">MPR Players:</h4>
            <div className="flex flex-wrap gap-2">
              {playerSummary.mprPlayers.map((player) => (
                <span
                  key={player.id}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                >
                  <PlayerNameWithIcon 
                    player={player} 
                    className="text-xs"
                    iconClassName="h-3 w-3 text-amber-700"
                  />
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Formation
              </label>
              <select
              value={currentFormation}
                onChange={handleFormationChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              >
                <option value="">Select Formation</option>
              {availableFormations.map((formation) => (
                <option key={formation.id} value={formation.id}>
                      {formation.name}
                    </option>
                  ))}
              </select>
            {availableFormations.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">
                No formations found in depth chart for {activeTab}
              </p>
            )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Strategy
              </label>
              <select
                value={currentStrategy}
                onChange={handleStrategyChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              >
                <option value="">Select Strategy</option>
                {SUBSTITUTION_STRATEGIES.map((strategy) => (
                  <option key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </option>
                ))}
              </select>
            </div>

          <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="mprOnly"
                  checked={mprOnly}
              onChange={handleMprToggle}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
            <label htmlFor="mprOnly" className="text-sm text-gray-700">
                  MPR Only (focus substitutions on Minimum Play Requirement positions)
                </label>
          </div>
        </div>
      </div>

      {/* Substitution Plan Display */}
      {currentPlan && (
        <div className="bg-white rounded-lg shadow">
          {/* Print Button */}
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Substitution Plan
            </h3>
            <button
              onClick={handlePrint}
              className="flex items-center bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </button>
          </div>
          
          {/* Printable Content */}
          <div id="substitution-plan-content">
            <SubstitutionReportComponent 
              plan={currentPlan}
              showHeader={false}
            />
          </div>
        </div>
      )}
    </div>
  );
} 